import React, {createContext, useCallback, useContext, useMemo, useRef} from 'react';
import type {FC, PropsWithChildren, RefObject} from 'react';

import {useFormHandlers} from "./useFormHandlers";
import type {InitialState, Store} from "./state";
import type {GValidators} from "./validations";
import type {GInputInitialState, GInputProps, GInputState, RNGInputProps} from './fields';
import type {GDOMElement} from './form';
import {_buildInputInitialValues, _clearDebounce} from "./helpers";

const GFormContext = createContext<Store>({} as Store);

type GFormContextProviderProps = PropsWithChildren & {
    initialState: InitialState;
    validators?: GValidators;
    optimized?: boolean;
    formRef?: RefObject<HTMLFormElement | null>;
};

export const GFormContextProvider: FC<GFormContextProviderProps> = ({
    children,
    initialState,
    validators,
    optimized = false,
    formRef
}) => {
    const stateRef = useRef(initialState);
    const listeners = useRef<Set<() => void>>(null);

    const setState = useCallback((updater: InitialState | ((state: InitialState) => InitialState)) => {
        const prevState = stateRef.current;
        const nextState =
            typeof updater === "function" ? (updater as (s: InitialState) => InitialState)(prevState) : updater;

        if (Object.is(prevState, nextState)) {
            return;
        }

        stateRef.current = nextState;
        listeners.current!.forEach((listener) => listener());
    }, []);

    const handlers = useFormHandlers(() => stateRef.current, setState, validators);

    const getInputElement = useCallback((formKey: string) => {
        if (!formRef || !formRef.current) return;
        // Use `elements.namedItem` instead of `formRef.current[formKey]`: the latter is
        // shadowed by HTMLFormElement's own properties, so a field named `submit`, `length`,
        // `action`, `method`, etc. would resolve to the wrong thing (e.g. the submit() method).
        return (formRef.current.elements.namedItem(formKey) ?? undefined) as unknown as GDOMElement;
    }, []);

    const registerField = useCallback((config: GInputProps | RNGInputProps) => {
        /* mutate stateRef without notifying listeners
         it is safe because this field didn't exist (no component was subscribed to it) */
        const prev = stateRef.current;

        if (prev.fields[config.formKey]) {
            if (__DEV__) {
                console.warn(`DEV ONLY - [Duplicate Keys] - field with key '${config.formKey}' already defined.`);
            }
            return;
        }

        const inputState = _buildInputInitialValues(config as GInputInitialState);

        inputState.checkValidity = () => {
            const input = stateRef.current.fields[inputState.formKey] as GInputState<any>;
            const el = getInputElement(inputState.formKey);
            // read `validity.valid` (not `checkValidity()`) so the native check fires no `invalid`
            // event — validation runs through exactly one path: the explicit `_viHandler` below.
            if (el && !el.validity.valid) return false; // native first (RN skips)
            handlers._viHandler(input);                 // run custom validation on the live field
            return !input.error;                        // valid = true
        };

        // Fields that mount with a value get their constraint validation baked in now, so
        // constraint errors render on the first paint (no follow-up re-render). Custom/async
        // validation still runs in the field's mount effect (with the full field set).
        if (inputState.value) {
            inputState.touched = true;
            handlers._checkConstraints(inputState);
        }

        // snapshot the resettable state so a native <form> reset can restore it (handlers._resetForm)
        inputState._initial = {
            value: inputState.value,
            error: inputState.error,
            errorText: inputState.errorText,
            touched: inputState.touched,
            dirty: inputState.dirty,
        };

        stateRef.current = {
            ...prev,
            fields: {
                ...prev.fields,
                [config.formKey]: {
                    ...inputState,
                    dispatchChanges: (changes: Partial<GInputState>, options?: {validate?: boolean}) =>
                        options?.validate
                            ? handlers._dispatchAndValidate(changes, config.formKey)
                            : handlers._dispatchChanges(changes, config.formKey)
                }
            }
        };
        // No listeners.current.forEach() here - the subscribing component
        // will read the updated stateRef immediately via useSyncExternalStore
    }, [handlers]);

    const unregisterField = useCallback((formKey: string) => {
        const prev = stateRef.current;
        const removed = prev.fields[formKey];
        if (!removed) return;

        // cancel & drop any pending debounce timers for this field so the timer map
        // doesn't grow unbounded (and to avoid a fetch firing after unmount)
        _clearDebounce(`${removed.gid}-fetch`, `${removed.gid}-async`);

        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const {[formKey]: _, ...remainingFields} = prev.fields;

        stateRef.current = {
            ...prev,
            fields: remainingFields
        };

        // Notify listeners so other components (e.g. submit button) re-render
        // with the updated isValid/isInvalid state
        listeners.current!.forEach(listener => listener());
    }, []);

    const store = useMemo<Store>(() => {
        if (!listeners.current) {
            listeners.current = new Set();
            stateRef.current = initialState;
            for (const fieldKey in initialState.fields) {
                initialState.fields[fieldKey].dispatchChanges = (changes: Partial<GInputState>, options?: {validate?: boolean}) =>
                    options?.validate
                        ? handlers._dispatchAndValidate(changes, fieldKey)
                        : handlers._dispatchChanges(changes, fieldKey);
            }
        } else {
            if (__DEBUG__) {
                console.log(`[form-context] - form changed state from`, stateRef.current, '\nto\n', initialState);
            }
            listeners.current.clear();
            for (const fieldKey in stateRef.current.fields) {
                stateRef.current.fields[fieldKey].dispatchChanges = (changes: Partial<GInputState>, options?: {validate?: boolean}) =>
                    options?.validate
                        ? handlers._dispatchAndValidate(changes, fieldKey)
                        : handlers._dispatchChanges(changes, fieldKey);
            }
        }

        return {
            getState: () => stateRef.current,
            setState,
            subscribe: (listener: () => void) => {
                listeners.current!.add(listener);
                return () => listeners.current!.delete(listener);
            },
            handlers,
            registerField,
            unregisterField,
            getInputElement,
            optimized
        };
    }, [initialState, optimized]);

    return <GFormContext.Provider value={store}>{children}</GFormContext.Provider>;
};

export const useFormStore = () => {
    const store = useContext(GFormContext);
    if (!store.getState) throw new Error('useFormStore must be used within `GForm` component');

    return store;
};